import React, {useCallback, useEffect, useState} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import {XpertKnowledgeSectionDto, XpertKnowledgeSubSectionDto} from '../../../../services/XpertKnowledgeSection/type'
import {ReactComponent as DeleteIcon} from '../../../../common/assets/delete-fill-icon.svg'
import {ReactComponent as AddIcon} from '../../../../common/assets/add-fill-icon.svg'
import XpertKnowledgeTutorialEditSubSection from './XpertKnowledgeTutorialEditSubSection'
import DynamicFormField from '../../../../components/FormField'
import QuickConfirmDialog from '../../../../components/QuickConfirmDialog'
import {useXpertKnowledgeTutorialContext} from '../../XpertKnowledgeTutorialContext'
import FormInput from '../../../../components/FormInput'
import {Tooltip} from '@mui/material'
import {SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable'
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {createPortal} from 'react-dom'
import i18n from '../../../../common/i18n'

interface XpertKnowledgeTutorialEditSectionProps {
  xpertKnowledgeSection: XpertKnowledgeSectionDto
  index: number
  onSectionChange: (updatedSection: XpertKnowledgeSectionDto) => void
  onDeleteSection: (sectionId: number) => void
  onDeleteSubSection: (sectionSubId: number) => void
  onOpenDeleteConfirm: (value: boolean) => void
}

const XpertKnowledgeTutorialEditSection: React.FC<XpertKnowledgeTutorialEditSectionProps> = ({
  xpertKnowledgeSection,
  index,
  onSectionChange,
  onDeleteSection,
  onDeleteSubSection,
  onOpenDeleteConfirm,
}) => {
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const {deleteXpertKnowledgeTutorialSection} = useXpertKnowledgeTutorialContext()
  const [isHovered, setIsHovered] = useState(false)
  const [draggedSubSection, setDraggedSubSection] = useState<XpertKnowledgeSubSectionDto | null>(null)
  const [sectionData, setSectionData] = useState<XpertKnowledgeSectionDto>(xpertKnowledgeSection)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const onDragStart = useCallback((event: DragStartEvent) => {
    if (event.active.data.current && 'xpertKnowledgeSubSection' in event.active.data.current) {
      setDraggedSubSection(event.active.data.current.xpertKnowledgeSubSection)
    }
  }, [])

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event
      if (over) {
        const activeId = active.id
        const overId = over.id
        const currentSubSections = [...(sectionData.subSections || [])]
        const activeIndex = currentSubSections.findIndex((sub) => sub.id === activeId)
        const overIndex = currentSubSections.findIndex((sub) => sub.id === overId)
        if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
          const [draggedItem] = currentSubSections.splice(activeIndex, 1)

          currentSubSections.splice(overIndex, 0, draggedItem)

          currentSubSections.forEach((subSection, index) => {
            subSection.position = index + 1
          })
          const updatedSection = {...sectionData, subSections: currentSubSections}
          setSectionData(updatedSection)
          onSectionChange(updatedSection)
        }
      }
      setDraggedSubSection(null)
    },
    [sectionData, onSectionChange]
  )

  useEffect(() => {
    setSectionData(xpertKnowledgeSection)
  }, [xpertKnowledgeSection])

  const handleFieldChange = (field: string, value: string) => {
    const updated = {...sectionData, [field]: value}
    setSectionData(updated)
    onSectionChange(updated)
  }

  const handleSubSectionChange = (updatedSubSection: XpertKnowledgeSubSectionDto) => {
    const updatedSubSections = sectionData.subSections?.map((sub: XpertKnowledgeSubSectionDto) =>
      sub.id === updatedSubSection.id ? updatedSubSection : sub
    )
    const updated = {...sectionData, subSections: updatedSubSections}
    setSectionData(updated)
    onSectionChange(updated)
  }

  const handleAddSubSection = () => {
    const maxPosition = sectionData.subSections?.reduce((max, sub) => Math.max(max, sub.position), 0) ?? 0

    const newSubSection: XpertKnowledgeSubSectionDto & {isNew?: boolean} = {
      id: Date.now(),
      title: 'New Sub Section',
      content: '',
      position: maxPosition + 1,
      isNew: true,
      medias: [],
    }

    const updated = {
      ...sectionData,
      subSections: sectionData.subSections ? [...sectionData.subSections, newSubSection] : [newSubSection],
    }
    setSectionData(updated)
    onSectionChange(updated)
  }

  return (
    <Col gap="20px" backgroundColor="#FFFFFF" padding="24px 28px" borderRadius="10px">
      <Row justifyContent="space-between" alignItems="center">
        <Block fontWeight={500} lineHeight="34px" fontSize="24px">
          {i18n.t('xk.tutorial.edit.sections.section', {number: index + 1})}
        </Block>
        <Block
          props={{
            onClick: () => {
              setOpenDeleteConfirm(true)
              onOpenDeleteConfirm(true)
            },
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          }}
          style={{cursor: 'pointer'}}
        >
          <Tooltip title={i18n.t('xk.tutorial.edit.sections.deleteSectionTooltip')} arrow>
            <DeleteIcon width={28} height={28} color={isHovered ? '#B01A27' : '#C71E2C'} />
          </Tooltip>
        </Block>
      </Row>
      <Col gap="8px">
        <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
          {i18n.t('xk.tutorial.edit.sections.fields.title')}
        </Block>
        <DynamicFormField
          placeholder={i18n.t('xk.tutorial.edit.sections.fields.titlePlaceholder')}
          value={xpertKnowledgeSection.title || ''}
          onEdit={(value) => handleFieldChange('title', value)}
        />
      </Col>
      <Col gap="8px">
        <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
          {i18n.t('xk.tutorial.edit.sections.fields.intro')}
        </Block>
        <FormInput
          placeholder={i18n.t('xk.tutorial.edit.sections.fields.introPlaceholder')}
          value={xpertKnowledgeSection.intro || ''}
          onSubmit={(value) => handleFieldChange('intro', value)}
          debounce={0}
          richTextEnabled={true}
        />
      </Col>
      <Col gap="8px">
        <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
          {i18n.t('xk.tutorial.edit.sections.fields.conclusion')}
        </Block>
        <FormInput
          placeholder={i18n.t('xk.tutorial.edit.sections.fields.conclusionPlaceholder')}
          value={xpertKnowledgeSection.conclusion || ''}
          onSubmit={(value) => handleFieldChange('conclusion', value)}
          debounce={0}
          richTextEnabled={true}
        />
      </Col>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <SortableContext items={sectionData.subSections ?? []} id={xpertKnowledgeSection.id.toString()} strategy={verticalListSortingStrategy}>
          {sectionData.subSections?.map((subSection, index) => (
            <XpertKnowledgeTutorialEditSubSection
              key={subSection.id}
              xpertKnowledgeSubSection={subSection}
              index={index}
              xpertKnowledgeSectionId={xpertKnowledgeSection.id}
              onSubSectionChange={handleSubSectionChange}
              onDeleteSubSection={onDeleteSubSection}
              onOpenDeleteConfirm={onOpenDeleteConfirm}
            />
          ))}
        </SortableContext>
        {createPortal(
          <DragOverlay>
            {draggedSubSection && (
              <XpertKnowledgeTutorialEditSubSection
                xpertKnowledgeSubSection={draggedSubSection}
                xpertKnowledgeSectionId={xpertKnowledgeSection.id}
                index={0}
                onSubSectionChange={handleSubSectionChange}
                onDeleteSubSection={onDeleteSubSection}
                onOpenDeleteConfirm={onOpenDeleteConfirm}
              />
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      <Row
        gap="10px"
        alignItems="center"
        justifyContent="flex-end"
        cursor="pointer"
        props={{
          onClick: () => handleAddSubSection(),
        }}
      >
        <Tooltip title={i18n.t('xk.tutorial.edit.actions.addSubSectionTooltip')} arrow>
          <AddIcon width={28} height={28} color="#2C9949" />
        </Tooltip>
      </Row>
      <QuickConfirmDialog
        key={xpertKnowledgeSection.id}
        open={openDeleteConfirm}
        setOpen={() => {
          setOpenDeleteConfirm(false)
          onOpenDeleteConfirm(false)
        }}
        title={i18n.t('xk.tutorial.edit.sections.quickDelete.confirmTitle')}
        content={i18n.t('xk.tutorial.edit.sections.quickDelete.confirmContent')}
        buttons={[
          {
            title: i18n.t('xk.tutorial.edit.sections.quickDelete.buttonDelete'),
            onClick: () => {
              if ((xpertKnowledgeSection as any).isNew) onDeleteSection(xpertKnowledgeSection.id)
              else deleteXpertKnowledgeTutorialSection(xpertKnowledgeSection.id)
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#F1164A',
              hoverBackgroundColor: '#C41E2C',
              color: '#FFFFFF',
            },
          },
        ]}
      />
    </Col>
  )
}

export default XpertKnowledgeTutorialEditSection
